import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { keyframes } from '@mui/system';

// Vuxen: kraftigare/snabbare puls. Barn: lugnare amplitud + längre cykel (NPF).
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const pulseChild = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.75; }
  100% { transform: scale(1); opacity: 1; }
`;

type VoiceRecorderProps = {
  transcript: string;
  onDone: () => void;
  onCancel: () => void;
  /** Barnläge: större touch-targets, mjukare puls. Default false = vuxen. */
  child?: boolean;
};

const VoiceRecorder = ({ transcript, onDone, onCancel, child = false }: VoiceRecorderProps) => {
  // cv = "child value": välj barnvärde om child, annars vuxenvärde (oförändrat).
  const cv = <T,>(childValue: T, adultValue: T): T => (child ? childValue : adultValue);

  const childButtonSx = child
    ? { minHeight: 52, borderRadius: '14px', fontSize: 16, fontWeight: 700 }
    : undefined;

  return (
    <Stack alignItems="center" spacing={3} py={3}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: cv(112, 96),
          height: cv(112, 96),
        }}
      >
        <CircularProgress
          size={cv(112, 96)}
          thickness={cv(3, 2)}
          sx={{
            position: 'absolute',
            color: 'primary.main',
            animation: `${cv(pulseChild, pulse)} ${cv('2s', '1.5s')} ease-in-out infinite`,
            // A11y: respektera reducerad rörelse i BÅDA lägena (saknades tidigare).
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Box
          sx={{
            width: cv(88, 72),
            height: cv(88, 72),
            borderRadius: '50%',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MicIcon sx={{ color: 'primary.contrastText', fontSize: cv(44, 36) }} />
        </Box>
      </Box>

      <Typography
        variant={cv(undefined, 'h6')}
        fontWeight={cv(700, 600)}
        sx={child ? { fontSize: 20 } : undefined}
      >
        Jag lyssnar...
      </Typography>

      {transcript && (
        <Box
          sx={{
            width: '100%',
            maxHeight: 120,
            overflowY: 'auto',
            bgcolor: cv('grey.50', 'grey.100'),
            borderRadius: cv<string | number>('14px', 2),
            px: 2,
            py: 1.5,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={child ? { fontSize: 17, lineHeight: 1.6 } : undefined}
          >
            {transcript}
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={2} width="100%">
        <Button variant="text" onClick={onCancel} fullWidth sx={childButtonSx}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={onDone}
          fullWidth
          disabled={!transcript.trim()}
          sx={childButtonSx}
        >
          Klar
        </Button>
      </Stack>
    </Stack>
  );
};

export default VoiceRecorder;
